'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Button } from 'src/components';
import { useDeleteFilterSetMutation } from 'src/api/redux/filterSets';
import {
  ParentFilterGroup,
  ParentFilterType,
} from 'src/context/reducers/filterSetReducer';
import { LOCATION } from 'src/hooks/filters/constants';
import useFilterSets from 'src/hooks/filters/useFilterSets';

interface DeleteFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterSet: ParentFilterGroup;
}

const DeleteFilterModal: React.FC<DeleteFilterModalProps> = ({
  isOpen,
  onClose,
  filterSet,
}) => {
  const {
    tempSelectedFilterPreset,
    appliedFilterPreset,
    clearFiltersForParentType,
    applyFilters,
    clearAllFilters,
  } = useFilterSets(LOCATION.DISCOVERY);

  // RTK Query mutation hook - cache invalidation happens automatically
  const [deleteFilterSet, { isLoading }] = useDeleteFilterSetMutation();
  // Store ID and type in state to ensure they persist even if props change
  const [storedId, setStoredId] = useState<number | null>(null);
  const [storedType, setStoredType] = useState<string | null>(null);

  // Update stored values whenever filterSet changes
  useEffect(() => {
    if (filterSet?.id) {
      setStoredId(filterSet.id);
    }
    if (filterSet?.type) {
      setStoredType(filterSet.type);
    }
  }, [filterSet]);
  const isSet = useMemo(() => {
    if (filterSet?.type !== 'FILTER') return false;

    // Check if the current filter set is active
    const activeId = tempSelectedFilterPreset?.id ?? appliedFilterPreset?.id;

    return activeId?.toString() === filterSet.id.toString();
  }, [filterSet, tempSelectedFilterPreset, appliedFilterPreset]);

  const handleDelete = async () => {
    // Use the stored ID and type from state
    if (!storedId) {
      return;
    }

    const shouldClearFilters = isSet && storedType;

    try {
      // Delete the filter set using RTK Query mutation
      // Cache invalidation happens automatically via invalidatesTags
      await deleteFilterSet({ filterId: storedId }).unwrap();

      // Only after successful deletion, clear the filters if needed
      if (shouldClearFilters && storedType) {
        // Cast storedType to ParentFilterType
        clearFiltersForParentType(storedType as ParentFilterType);
      }

      // Clear all filters to ensure the PresetsDropdown is updated
      if (storedType) {
        clearAllFilters(storedType as ParentFilterType);
      }

      applyFilters();
      onClose();
    } catch (error) {
      // Silent error handling - errors will be logged by the API layer
      console.error('Error deleting filter set:', error);
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus the first interactive element
      const firstButton = document.querySelector('button');
      if (firstButton) {
        (firstButton as HTMLElement).focus();
      }
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Create the modal content
  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center w-full h-full px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-filter-title"
      aria-describedby="delete-filter-description"
      data-testid="delete-filter-modal"
    >
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
        data-testid="delete-filter-modal-backdrop"
      />
      <div
        className="relative w-full max-w-md bg-white shadow-xl flex flex-col overflow-hidden rounded-lg"
        role="document"
        data-testid="delete-filter-modal-content"
      >
        <div className="flex items-center justify-between p-4 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center"
              aria-hidden="true"
              data-testid="delete-filter-modal-icon"
            >
              <Icon icon={faTrash} className="text-red-500 text-sm" />
            </div>
            <h2
              id="delete-filter-title"
              className="text-lg font-semibold text-primary"
              data-testid="delete-filter-modal-title"
            >
              Delete Filter
            </h2>
          </div>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onClose}
            className="hover:bg-neutral-50"
            aria-label="Close delete filter dialog"
            data-testid="delete-filter-modal-close-button"
          >
            <Icon icon={faXmark} />
          </Button>
        </div>

        <div className="flex flex-col gap-6 p-6">
          <p
            id="delete-filter-description"
            className="text-neutral-600 text-sm font-medium"
            data-testid="delete-filter-modal-description"
          >
            Are you sure you want to delete &ldquo;{filterSet?.label}&rdquo;?
            This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="min-w-24 rounded-full"
              data-testid="delete-filter-modal-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDelete}
              className="min-w-24 rounded-full"
              loading={isLoading}
              aria-busy={isLoading}
              data-testid="delete-filter-modal-delete-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Always render to document.body for maximum compatibility
  return createPortal(modalContent, document.body);
};

export default DeleteFilterModal;
